import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Scale, Loader2, EyeOff, Headphones, FileText, Brain, ArrowRight } from "lucide-react";
import { API } from "../App";
import { PreviewPlayer } from "../components/preview/PreviewPlayer";
import { LeadSheetCard } from "../components/preview/LeadSheetCard";
import { absUrl, adminHeaders, trackPreview, visitContext } from "../components/preview/previewApi";

const STEPS = [
  { icon: Headphones, title: "Listen", text: "Hear the rule set to an original song built for recall." },
  { icon: FileText, title: "Follow along", text: "Track each element on the matching one-page Lead Sheetz chart." },
  { icon: Brain, title: "Remember", text: "Lyrics and visuals together help the rule stick on exam day." },
];

const Brand = () => (
  <div className="flex items-center gap-2" data-testid="preview-brand">
    <Scale className="h-5 w-5 text-primary" />
    <span className="font-serif text-xl font-bold">Bar Songz</span>
  </div>
);

const ExploreLink = ({ className = "" }) => (
  <Link to="/" onClick={() => trackPreview("cta_click")} className={`group inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-primary ${className}`} data-testid="preview-explore-link">
    Explore the full Bar Songz platform
    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
  </Link>
);

const Unavailable = ({ admin }) => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center" data-testid="preview-unavailable">
    <Brand />
    <p className="max-w-sm text-muted-foreground">
      {admin
        ? "Review mode: choose a sample song in Admin → Preview Page to see this page."
        : "This preview isn't available right now. Please check back soon."}
    </p>
    <ExploreLink />
  </div>
);

export default function SongPreviewPage() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const tracked = useRef(false);

  useEffect(() => {
    document.title = "Free Preview | Bar Songz";
    axios
      .get(`${API}/preview-page`, { headers: adminHeaders() })
      .then((r) => {
        setData(r.data);
        setState("ready");
        if (!tracked.current && r.data.published && !r.data.admin_view) {
          tracked.current = true;
          trackPreview("visit", visitContext());
        }
      })
      .catch(() => setState("unavailable"));
  }, []);

  if (state === "loading") {
    return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  }
  if (state === "unavailable" || !data?.song) return <Unavailable admin={data?.admin_view} />;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="song-preview-page">
      {data.admin_view && !data.published && (
        <div className="flex items-center justify-center gap-2 bg-amber-100 px-4 py-2 text-center text-xs font-medium text-amber-900" data-testid="preview-review-banner">
          <EyeOff className="h-4 w-4 shrink-0" />
          Review mode: this page is hidden from the public until you click Publish in Admin → Preview Page.
        </div>
      )}
      <header className="container mx-auto flex items-center justify-between px-6 py-5 lg:px-12">
        <Brand />
        <span className="rounded-full border border-primary/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">Free preview</span>
      </header>

      <main className="container mx-auto px-6 pb-16 lg:px-12">
        <section className="max-w-3xl pb-10 pt-6 lg:pt-12 animate-in fade-in slide-in-from-bottom-2 duration-700">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-primary">No sign-up needed</p>
          <h1 className="font-serif text-4xl font-bold leading-[1.05] sm:text-5xl lg:text-6xl">
            Law School, <span className="italic text-primary">Remixed.</span>
          </h1>
          <p className="mt-5 text-base leading-relaxed text-foreground/80 md:text-lg">
            Bar Songz is a supplemental legal learning resource that pairs original music with companion
            Legal Lead Sheetz™, one-page visual study charts. Press play, review the chart,
            and see how the rule sticks.
          </p>
        </section>

        <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-12">
          <div className="space-y-8 lg:sticky lg:top-8">
            <PreviewPlayer src={absUrl(data.audio_url)} song={data.song} fadeStart={data.fade_start} />
            <ol className="space-y-5" data-testid="preview-steps">
              {STEPS.map(({ icon: Icon, title, text }, i) => (
                <li key={title} className="flex gap-4">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-secondary text-primary"><Icon className="h-5 w-5" /></span>
                  <div>
                    <p className="font-serif text-lg font-semibold leading-tight">{i + 1}. {title}</p>
                    <p className="text-sm text-muted-foreground">{text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          {data.sheet ? (
            <LeadSheetCard sheet={data.sheet} songTitle={data.song.title} />
          ) : (
            <div className="rounded-3xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">Lead Sheetz coming soon.</div>
          )}
        </div>
      </main>

      <footer className="border-t border-border bg-secondary/30">
        <div className="container mx-auto flex flex-col gap-3 px-6 py-8 text-xs text-muted-foreground lg:flex-row lg:items-center lg:justify-between lg:px-12">
          <p className="max-w-xl leading-relaxed">
            Bar Songz is a supplemental study aid and is not a substitute for your coursework, bar preparation program, or legal advice.
            © 2026 Bar Songz. All rights reserved.
          </p>
          <ExploreLink />
        </div>
      </footer>
    </div>
  );
}
